import { downloadPaymentsInvoice } from "core/Services/AccountantServices";
import { getUsers } from "core/Services/AdminServices";
import { getPayments } from "core/Services/MerchantServices";
import {
  ChevronDown,
  DownloadCloud,
  EyeIcon,
  Loader,
  Plus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { formatDate } from "utils/DatesUtil";
import AddPaymentDialog from "./AddPaymentDialog";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const AccountantPayments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [payouts, setPayouts] = useState<any>();
  const [merchants, setMerchants] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    number | null
  >(null);
  const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false);
  const { register, watch, reset } = useForm({
    defaultValues: {
      merchantId: "",
      dateFrom: "",
      dateTo: "",
    },
  });

  const handleClearFilters = () => {
    reset();
    fetchPayouts();
  };

  const fetchPayouts = async (
    filters?: { merchantId?: string; dateFrom?: string; dateTo?: string },
    page: number = 1
  ) => {
    try {
      setIsLoading(true);
      const params: Record<string, any> = {};

      // Add non-empty filters to params
      if (filters?.merchantId?.trim()) {
        params.merchant_id = filters.merchantId;
      }
      if (filters?.dateFrom?.trim()) {
        params.dateFrom = filters.dateFrom;
      }
      if (filters?.dateTo?.trim()) {
        params.dateTo = filters.dateTo;
      }

      // Always include page number
      params.page = page;

      const response = await getPayments(params);
      setPayouts(response);
    } catch (error) {
      toast.error(t("accountantPayments.messages.failedToLoadPayouts"));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const response = await getUsers({ role: "merchant", getAll: 1 });
        setMerchants(response.data);
      } catch (error) {
        toast.error(t("accountantPayments.messages.failedToLoadMerchants"));
      }
    };
    fetchMerchants();
    fetchPayouts();
  }, []);
  useEffect(() => {
    const subscription = watch((values) => {
      fetchPayouts(
        {
          merchantId: values.merchantId,
          dateFrom: values.dateFrom,
          dateTo: values.dateTo,
        },
        currentPage
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, currentPage]);
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (payouts?.meta.last_page || 1)) {
      setCurrentPage(page);
      fetchPayouts(
        {
          merchantId: watch("merchantId"),
          dateFrom: watch("dateFrom"),
          dateTo: watch("dateTo"),
        },
        page
      );
    }
  };

  const handlePaymentSuccess = () => {
    // Refresh the payments list
    fetchPayouts();
  };

  const handleDownloadInvoice = async (paymentId: number) => {
    try {
      setDownloadingInvoiceId(paymentId);
      await downloadPaymentsInvoice(paymentId);
      toast.success(t("accountantPayments.messages.invoiceDownloadSuccess"));
    } catch (error) {
      toast.error(t("accountantPayments.messages.invoiceDownloadFailed"));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  return (
    <>
      {/* Add Payment Dialog */}{" "}
      <AddPaymentDialog
        isOpen={isAddPaymentDialogOpen}
        onClose={() => setIsAddPaymentDialogOpen(false)}
        onSuccess={handlePaymentSuccess}
      />
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Add Payment Button */}
        <div className="flex justify-end">
          <Button
            onClick={() => setIsAddPaymentDialogOpen(true)}
            className="text-white px-4 py-2 rounded"
            icon={<Plus size={16} />}
          >
            {t("accountantPayments.addPayment")}
          </Button>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <FormInput
              name="merchantId"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("accountantPayments.selectMerchant")}
              register={register}
              selectOptions={merchants.map((merchant) => ({
                value: merchant.id,
                label: merchant.business_name,
              }))}
              errors={{}}
              label={t("accountantPayments.selectMerchant")}
            />
            <FormInput
              name="dateFrom"
              type="date"
              placeholder={t("accountantPayments.dateFrom")}
              register={register}
              errors={{}}
              label={t("accountantPayments.dateFrom")}
            />
            <FormInput
              name="dateTo"
              type="date"
              placeholder={t("accountantPayments.dateTo")}
              register={register}
              errors={{}}
              label={t("accountantPayments.dateTo")}
            />
            <div className="flex items-end">
              <Button
                onClick={handleClearFilters}
                className="text-gray-600  px-4 py-2 rounded"
              >
                {t("accountantPayments.clearFilters")}
              </Button>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {[
                  t("accountantPayments.table.date"),
                  t("accountantPayments.table.merchant"),
                  t("accountantPayments.table.amount"),
                  t("accountantPayments.table.status"),
                  t("accountantPayments.table.invoice"),
                  t("accountantPayments.table.actions"),
                ].map((header, index) => (
                  <th
                    key={index}
                    className="p-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading
                ? [...Array(5)].map((_, index) => (
                    <tr key={index}>
                      {[...Array(6)].map((_, colIndex) => (
                        <td key={colIndex} className="p-3">
                          <div className="skeleton h-4 w-20 bg-gray-200"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                : payouts?.data.map((payment: any) => (
                    <tr key={payment.id}>
                      <td className="p-3">{formatDate(payment.date)}</td>
                      <td className="p-3">
                        {payment.merchant?.business_name}
                      </td>{" "}
                      <td className="p-3">
                        JOD{" "}
                        {Number(payment.amount || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs  ${
                            getStatusColors(payment.status).bg
                          }
                          ${getStatusColors(payment.status).text}`}
                        >
                          {payment.status.charAt(0).toUpperCase() +
                            payment.status.slice(1)}
                        </span>
                      </td>
                      <td
                        className="p-3"
                        onClick={() =>
                          payment.amount < 0 &&
                          handleDownloadInvoice(payment.id)
                        }
                      >
                        {downloadingInvoiceId === payment.id ? (
                          <Loader className="animate-spin text-blue-600" />
                        ) : (
                          <DownloadCloud
                            className={
                              payment.amount < 0
                                ? "hover:text-blue-600 cursor-pointer"
                                : "text-gray-400 cursor-not-allowed"
                            }
                          />
                        )}
                      </td>
                      <td className="">
                        <Link
                          to={`/payment/${payment.id}?pageTitle=Payment Details`}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-500 mb-2 sm:mb-0">
              {t("accountantPayments.pagination.showing")}{" "}
              {payouts?.meta.from || 0}-{payouts?.meta.to || 0}{" "}
              {t("accountantPayments.pagination.of")} {payouts?.meta.total || 0}{" "}
              {t("accountantPayments.pagination.entries")}
            </div>
            <div className="flex space-x-2">
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                {t("accountantPayments.pagination.previous")}
              </button>
              {[...Array(payouts?.meta.last_page || 1)].map((_, index) => (
                <button
                  key={index}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === index + 1 ? "bg-blue-600 text-white" : ""
                  }`}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              <button
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                disabled={currentPage === payouts?.meta.last_page}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                {t("accountantPayments.pagination.next")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountantPayments;
